import React, { useState, useEffect } from 'react';
import AuditOverviewGraphContainer from '../../Containers/AuditOverviewGraph';
import { getAuditOverviewGraph, getorganization, getThemes } from '../../api/common';

type ViewMode = 'organization' | 'theme';
type TimePeriod = '1year' | '2year' | 'custom';

const AuditOverviewGraph: React.FC = () => {
    // State for filter selections
    const [viewMode, setViewMode] = useState<ViewMode>('organization');
    const [selectedOrganization, setSelectedOrganization] = useState<string>('');
    const [selectedTheme, setSelectedTheme] = useState<string>('');
    const [timePeriod, setTimePeriod] = useState<TimePeriod>('1year');
    const [customDateRange, setCustomDateRange] = useState<{ start: Date | null; end: Date | null }>({ start: null, end: null });
    const [isLoading, setIsLoading] = useState(false);

    // State for filter data
    const [organizationsList, setOrganizationsList] = useState<any[]>([]);
    const [themesList, setThemesList] = useState<any[]>([]);
    const [filtersLoaded, setFiltersLoaded] = useState(false);

    // State for modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedNodeData, setSelectedNodeData] = useState<any>(null);
    const [nodeToExpand, setNodeToExpand] = useState<any>(null);
    const [resetExpandedNodes, setResetExpandedNodes] = useState(false);

    // State for network graph data
    const [networkData, setNetworkData] = useState<any>(null);
    const [statsData, setStatsData] = useState({
        organizations: 0,
        themes: 0,
        audits: 0,
        compliant: 0,
        nonCompliant: 0
    });

    // Fetch organizations and themes on mount
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const [orgsResponse, themesResponse] = await Promise.all([
                    getorganization(),
                    getThemes()
                ]);

                if (orgsResponse) {
                    // Remove duplicates based on organization name
                    const uniqueOrgs = orgsResponse.filter((org: any, index: number, self: any[]) => {
                        const orgName = org.organizationName || org.name || '';
                        return index === self.findIndex((o: any) =>
                            (o.organizationName || o.name || '').toLowerCase() === orgName.toLowerCase()
                        );
                    });

                    // Sort organizations alphabetically by name
                    const sortedOrgs = uniqueOrgs.sort((a: any, b: any) => {
                        const nameA = (a.organizationName || a.name || '').toLowerCase();
                        const nameB = (b.organizationName || b.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                    setOrganizationsList(sortedOrgs);
                    // Set first organization as default (use name instead of ID)
                    if (sortedOrgs.length > 0) {
                        setSelectedOrganization(sortedOrgs[0].organizationName || sortedOrgs[0].name);
                    }
                }

                if (themesResponse) {
                    // Remove duplicates based on theme name
                    const uniqueThemes = themesResponse.filter((theme: any, index: number, self: any[]) => {
                        const themeName = theme.themeName || theme.name || '';
                        return index === self.findIndex((t: any) =>
                            (t.themeName || t.name || '').toLowerCase() === themeName.toLowerCase()
                        );
                    });

                    // Sort themes alphabetically by name
                    const sortedThemes = uniqueThemes.sort((a: any, b: any) => {
                        const nameA = (a.themeName || a.name || '').toLowerCase();
                        const nameB = (b.themeName || b.name || '').toLowerCase();
                        return nameA.localeCompare(nameB);
                    });
                    setThemesList(sortedThemes);
                    // Set first theme as default when in theme mode
                    if (sortedThemes.length > 0 && viewMode === 'theme') {
                        setSelectedTheme(sortedThemes[0].themeName || sortedThemes[0].name);
                    }
                }

                setFiltersLoaded(true);
            } catch (error) {
                console.error('Error fetching filters:', error);
                setFiltersLoaded(true); // Set to true anyway to prevent infinite loading
            }
        };

        fetchFilters();
    }, []);

    // Format date to match API requirements: YYYY-MM-DDTHH:mm:ss
    const formatDateForAPI = (date: Date, isEndDate = false): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');

        if (isEndDate) {
            return `${year}-${month}-${day}T23:59:59`;
        }
        return `${year}-${month}-${day}T00:00:00`;
    };

    // Get date range based on time period
    const getDateRange = (): { start: Date; end: Date } => {
        const end = new Date();
        const start = new Date();

        if (timePeriod === '1year') {
            start.setFullYear(end.getFullYear() - 1);
        } else if (timePeriod === '2year') {
            start.setFullYear(end.getFullYear() - 2);
        } else if (timePeriod === 'custom' && customDateRange.start && customDateRange.end) {
            return { start: customDateRange.start, end: customDateRange.end };
        } else {
            // Default to 1 year if custom is selected but no dates chosen
            start.setFullYear(end.getFullYear() - 1);
        }

        return { start, end };
    };

    // Fetch data based on filters - This is where drilldown happens
    const fetchData = async () => {
        // Only fetch if filters are loaded
        if (!filtersLoaded) return;

        setIsLoading(true);
        try {
            // Determine the root type based on view mode
            const rootType = viewMode === 'organization' ? 'ORGANIZATION' : 'THEME';

            // Determine filter value based on selection
            const filterValue = viewMode === 'organization' ? selectedOrganization : selectedTheme;

            // Only call API if filter value is selected
            if (!filterValue) {
                setIsLoading(false);
                return;
            }

            // Get date range and format for API
            const dateRange = getDateRange();
            const fromDate = formatDateForAPI(dateRange.start, false);
            const toDate = formatDateForAPI(dateRange.end, true);

            // Call the API with date filters
            const response = await getAuditOverviewGraph(rootType, filterValue, fromDate, toDate);

            if (response) {
                setNetworkData(response);

                // Calculate stats from the network data
                calculateStats(response.network);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Calculate statistics from network data
    const calculateStats = (network: any[]) => {
        let organizations = 0;
        let themes = 0;
        let audits = 0;
        let compliant = 0;
        let nonCompliant = 0;

        const traverse = (nodes: any[]) => {
            nodes.forEach(node => {
                switch (node.nodeType) {
                    case 'ORGANIZATION':
                        organizations++;
                        break;
                    case 'THEME':
                        themes++;
                        break;
                    case 'AUDIT':
                        audits++;
                        break;
                    case 'COMPLIANT':
                        compliant += parseInt(node.name) || 0;
                        break;
                    case 'NON_COMPLIANT':
                        nonCompliant += parseInt(node.name) || 0;
                        break;
                }
                if (node.children) {
                    traverse(node.children);
                }
            });
        };

        traverse(network);
        setStatsData({ organizations, themes, audits, compliant, nonCompliant });
    };

    // Refetch data when filters change (Drilldown functionality)
    useEffect(() => {
        // Close modal and reset expanded nodes when filters change
        setIsModalOpen(false);
        setSelectedNodeData(null);
        setResetExpandedNodes(true);

        fetchData();

        // Reset the flag after a short delay
        setTimeout(() => setResetExpandedNodes(false), 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewMode, selectedOrganization, selectedTheme, timePeriod, customDateRange, filtersLoaded]);


    // Handle filter changes from OrganizationThemeSelector
    const handleViewModeChange = (mode: ViewMode) => {
        setViewMode(mode);
        // Set default selection when switching modes
        if (mode === 'organization') {
            setSelectedTheme('');
            // Set first organization as default
            if (organizationsList.length > 0) {
                setSelectedOrganization(organizationsList[0].organizationName || organizationsList[0].name);
            }
        } else {
            setSelectedOrganization('');
            // Set first theme as default
            if (themesList.length > 0) {
                setSelectedTheme(themesList[0].themeName || themesList[0].name);
            }
        }
    };

    const handleOrganizationChange = (organizationId: string) => {
        setSelectedOrganization(organizationId);
    };

    const handleThemeChange = (themeId: string) => {
        setSelectedTheme(themeId);
    };

    const handleTimePeriodChange = (period: TimePeriod) => {
        setTimePeriod(period);
    };

    const handleCustomDateChange = (startDate: Date, endDate: Date) => {
        setCustomDateRange({ start: startDate, end: endDate });
        setTimePeriod('custom');
    };

    // Handle node click to open modal
    const handleNodeClick = (nodeData: any) => {
        setSelectedNodeData(nodeData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedNodeData(null);
    };

    // Handle node expansion from modal navigation
    const handleNodeExpand = (node: any) => {
        setNodeToExpand(node);
        // Reset after a short delay to allow the effect to trigger
        setTimeout(() => setNodeToExpand(null), 100);
    };

    return (
        <AuditOverviewGraphContainer
            viewMode={viewMode}
            selectedOrganization={selectedOrganization}
            selectedTheme={selectedTheme}
            timePeriod={timePeriod}
            customDateRange={customDateRange}
            isLoading={isLoading}
            organizationsList={organizationsList}
            themesList={themesList}
            isModalOpen={isModalOpen}
            selectedNodeData={selectedNodeData}
            nodeToExpand={nodeToExpand}
            resetExpandedNodes={resetExpandedNodes}
            networkData={networkData}
            statsData={statsData}
            onViewModeChange={handleViewModeChange}
            onOrganizationChange={handleOrganizationChange}
            onThemeChange={handleThemeChange}
            onTimePeriodChange={handleTimePeriodChange}
            onCustomDateChange={handleCustomDateChange}
            onNodeClick={handleNodeClick}
            onCloseModal={handleCloseModal}
            onNodeExpand={handleNodeExpand}
        />
    );
};

export default AuditOverviewGraph;
